import { useState, useEffect } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Reveal } from "./Reveal";
import { Check, Star, Clock, X, Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import { useFooter } from "@/lib/useContent";

const PERIOD_LABEL = { mensual: "/ mes", anual: "/ año", unico: "" };

function priceText(t) {
  const amount = (t.price || "").toString().trim();
  if (!amount) return "Consultar";
  return `${t.currency || "$"} ${amount} ${PERIOD_LABEL[t.period] || ""}`.trim();
}

export const Membresias = () => {
  const [tiers, setTiers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pending, setPending] = useState(null);
  const contact = useFooter();
  const wa = (contact.whatsapp || "").replace(/\D/g, "");

  useEffect(() => {
    api.get("/memberships")
      .then(({ data }) => setTiers(data || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  return (
    <section id="socios" className="relative py-24 md:py-32 bg-[#eef1f7] grain-overlay">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <Reveal className="max-w-3xl mx-auto text-center">
          <span className="section-tag">Hacete socio</span>
          <h2 className="mt-4 text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-navy">
            Elegí tu categoría de socio
          </h2>
          <p className="mt-5 text-lg text-gray-600 leading-relaxed">
            Sumate a la familia del Comandante Leo Nowak. Cada categoría tiene sus beneficios.
          </p>
        </Reveal>

        {loading ? (
          <div className="mt-14 flex justify-center text-navy/40"><Loader2 className="h-7 w-7 animate-spin" /></div>
        ) : tiers.length === 0 ? (
          <p className="mt-14 text-center text-gray-400">Pronto vamos a publicar las categorías de socio.</p>
        ) : (
          <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-3 gap-6 items-stretch max-w-5xl mx-auto">
            {tiers.map((t, i) => (
              <Reveal key={t.id} delay={i * 0.08}>
                <div
                  className={`relative h-full flex flex-col bg-white rounded-2xl border-2 p-7 transition-all hover:-translate-y-1.5 ${
                    t.highlight ? "shadow-2xl" : "shadow-sm hover:shadow-lg"
                  }`}
                  style={{ borderColor: t.color, boxShadow: t.highlight ? `0 24px 50px -12px ${t.color}55` : undefined }}
                  data-testid={`tier-card-${i}`}
                >
                  {t.highlight && (
                    <span className="absolute -top-3.5 left-1/2 -translate-x-1/2 inline-flex items-center gap-1 text-xs font-bold uppercase tracking-wider text-navy bg-gold px-4 py-1.5 rounded-full shadow-md">
                      <Star className="h-3.5 w-3.5 fill-navy" /> Más elegida
                    </span>
                  )}
                  <div className="flex items-center gap-2.5">
                    <span className="h-4 w-4 rounded-full border-2 border-white shadow" style={{ backgroundColor: t.color }} />
                    <h3 className="text-xl font-bold text-navy">{t.name}</h3>
                  </div>
                  <div className="mt-4 text-2xl font-extrabold text-navy" style={{ fontFamily: "Outfit" }}>{priceText(t)}</div>
                  {t.description && <p className="mt-2 text-sm text-gray-500 leading-relaxed">{t.description}</p>}

                  <ul className="mt-6 space-y-3 flex-1">
                    {(t.perks || []).map((p, pi) => (
                      <li key={pi} className="flex items-start gap-2.5 text-sm text-gray-700">
                        <Check className="h-5 w-5 shrink-0" style={{ color: t.color }} />
                        <span>{p}</span>
                      </li>
                    ))}
                  </ul>

                  <button
                    data-testid={`tier-cta-${t.name.toLowerCase()}`}
                    onClick={() => setPending(t)}
                    className="mt-7 w-full py-3.5 rounded-xl font-bold text-center transition-all hover:opacity-90"
                    style={{ backgroundColor: t.color, color: ["Oro", "Plata"].includes(t.name) ? "#1a2a6c" : "#ffffff" }}
                  >
                    Elegir {t.name}
                  </button>
                </div>
              </Reveal>
            ))}
          </div>
        )}
      </div>

      {/* Próximamente popup */}
      <AnimatePresence>
        {pending && (
          <motion.div
            data-testid="membership-modal"
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            onClick={() => setPending(null)}
            className="fixed inset-0 z-[120] bg-navy/70 backdrop-blur-sm flex items-center justify-center p-4"
          >
            <motion.div
              initial={{ scale: 0.94, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.94, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              className="relative bg-white rounded-3xl max-w-md w-full p-8 text-center"
            >
              <button data-testid="membership-modal-close" onClick={() => setPending(null)} className="absolute top-4 right-4 h-9 w-9 rounded-lg bg-gray-100 hover:bg-gray-200 flex items-center justify-center text-navy">
                <X className="h-5 w-5" />
              </button>
              <div className="mx-auto h-16 w-16 rounded-2xl flex items-center justify-center" style={{ backgroundColor: `${pending.color}22`, color: pending.color }}>
                <Clock className="h-8 w-8" />
              </div>
              <span className="mt-5 inline-block text-xs font-bold uppercase tracking-wider text-gold">Próximamente</span>
              <h3 className="mt-2 text-2xl font-bold text-navy" style={{ fontFamily: "Outfit" }}>
                Socio {pending.name}
              </h3>
              <p className="mt-3 text-gray-600 leading-relaxed">
                Estamos preparando la suscripción online para la categoría <b>{pending.name}</b> ({priceText(pending)}). Muy pronto vas a poder asociarte desde acá en pocos pasos.
              </p>
              <p className="mt-4 text-sm text-gray-500">
                Mientras tanto, escribinos y te ayudamos a asociarte.
              </p>
              <a
                href={`https://wa.me/${wa}?text=${encodeURIComponent(`Hola! Quiero asociarme como Socio ${pending.name} del Club Comandante Leo Nowak.`)}`}
                target="_blank" rel="noreferrer" data-testid="membership-modal-contact"
                className="mt-6 inline-flex w-full items-center justify-center gap-2 bg-navy text-white font-semibold py-3.5 rounded-xl hover:bg-[#16235a] transition-colors"
              >
                Consultar por WhatsApp
              </a>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};
